import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import type { Cell } from '../demo/seed';
import { fmtTon, fmtCellId, fmtAddress, fmtRelativeTime } from '../lib/format';
import { labelForTier, colorForTier } from '../lib/survival';
import { Sparkline } from './Sparkline';
import { StakeForm } from './StakeForm';

type Props = {
  cell: Cell | null;
  onStaked: (cellId: number, amount: number) => void;
};

export function CellDetailPanel({ cell, onStaked }: Props) {
  const [promptOpen, setPromptOpen] = useState(false);

  if (!cell) {
    return (
      <div className="p-6 text-xs text-text-mono-dim">
        Select a cell to inspect it.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 space-y-4 text-text-primary text-xs overflow-y-auto h-full">
      <div className="space-y-1">
        <div className="text-text-mono-dim">Cell {fmtCellId(cell.id)}</div>
        <div className="font-display tracking-tightest text-md">{cell.agent?.name ?? 'empty'}</div>
      </div>

      <div
        className="inline-block px-2 py-1 rounded-sm font-mono uppercase tracking-tightest"
        style={{
          color: colorForTier(cell.state),
          border: `1px solid ${colorForTier(cell.state)}`,
        }}
      >
        {labelForTier(cell.state)}
      </div>

      {cell.agent && (
        <>
          <div className="space-y-1">
            <div className="text-text-mono-dim text-2xs">BALANCE</div>
            <div className="text-xl font-mono tabular">◊ {fmtTon(cell.agent.balance)} TON</div>
            <Sparkline values={cell.agent.sparkline} width={280} height={42} />
          </div>

          <div className="space-y-1">
            <button
              className="flex items-center gap-1 text-text-mono-dim text-2xs"
              onClick={() => setPromptOpen((p) => !p)}
            >
              {promptOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              GENESIS PROMPT
            </button>
            <div
              className={`hairline p-2 bg-panel font-mono text-2xs leading-snug ${
                promptOpen ? '' : 'line-clamp-3'
              }`}
            >
              {cell.agent.genesisPrompt}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-text-mono-dim text-2xs">TELEGRAM</div>
            <a
              href={`https://t.me/${cell.agent.telegramHandle.replace('@', '')}`}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-1 text-blue-life hover:text-blue-glow"
            >
              {cell.agent.telegramHandle}
              <ExternalLink size={10} />
            </a>
          </div>

          <div className="space-y-1">
            <div className="text-text-mono-dim text-2xs">LINEAGE</div>
            <div className="flex items-center gap-1 flex-wrap font-mono">
              <span>Gen {cell.agent.generation}</span>
              {cell.agent.parentId != null && (
                <>
                  <span>·</span>
                  <span>Parent:</span>
                  <Link to={`/lineage/${cell.agent.parentId}`} className="text-blue-life hover:text-blue-glow">
                    {fmtCellId(cell.agent.parentId)}
                  </Link>
                </>
              )}
              <span>·</span>
              <span>Children: {cell.agent.childrenIds.length}</span>
            </div>
            <Link
              to={`/lineage/${cell.id}`}
              className="inline-block text-2xs text-blue-life hover:text-blue-glow"
            >
              View lineage tree →
            </Link>
          </div>

          <div className="space-y-1">
            <div className="text-text-mono-dim text-2xs">TON WALLET</div>
            <div className="flex items-center gap-2 font-mono text-2xs">
              <span>{fmtAddress(cell.agent.walletAddress)}</span>
              <button
                onClick={() => navigator.clipboard.writeText(cell.agent!.walletAddress)}
                className="text-text-secondary hover:text-text-primary"
                aria-label="Copy address"
              >
                <Copy size={11} />
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-text-mono-dim text-2xs">RECENT EARNINGS</div>
            <table className="w-full text-2xs font-mono">
              <thead className="text-text-mono-dim text-left">
                <tr>
                  <th className="font-normal pb-1">TIME</th>
                  <th className="font-normal pb-1">SOURCE</th>
                  <th className="font-normal pb-1 text-right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {cell.agent.recentEvents.slice(0, 5).map((ev, i) => (
                  <tr key={i} className="border-t border-grid-line">
                    <td className="py-1 text-text-secondary">{fmtRelativeTime(ev.ts)}</td>
                    <td className="py-1">{ev.source}</td>
                    <td className="py-1 text-right text-green-thrive">+{fmtTon(ev.amount, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2 pt-2 border-t border-grid-line">
            <div className="text-text-mono-dim text-2xs">STAKE ON THIS CELL</div>
            <StakeForm cell={cell} onStaked={(amt) => onStaked(cell.id, amt)} />
          </div>

          <a
            href={`https://tonscan.org/address/${cell.agent.walletAddress}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 text-2xs text-blue-life hover:text-blue-glow"
          >
            View on TONScan
            <ExternalLink size={10} />
          </a>
        </>
      )}
    </div>
  );
}
